import { Inject, Injectable, Injector, LOCALE_ID, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LoggerService } from '@klickdata/core/application/src/logger/logger.service';
import { ConfigService } from '@klickdata/core/config';
import { Customer, CustomerData, CustomerLanding } from '@klickdata/core/customer/src/customer.model';
import { HttpErrorService } from '@klickdata/core/http/src/error/http-error.service';
import { RequestBuilderService } from '@klickdata/core/http/src/request/request-builder.service';
import { ResponseData } from '@klickdata/core/http/src/responce/responce';
import { Language } from '@klickdata/core/localization';
import { UserLogin } from '@klickdata/core/user/src/user-login.model';
import { User, UserData } from '@klickdata/core/user/src/user.model';
import { CacheUtils, Utils } from '@klickdata/core/util';
import * as Sentry from '@sentry/browser';
import { DialogTokenExpirationCountdownComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-token-expiration-countdown/dialog-token-expiration-countdown.component';
import moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { LocalStorageService } from 'ngx-webstorage';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subscription,
    combineLatest,
    defer,
    fromEvent,
    interval,
    merge,
    of,
} from 'rxjs';
import {
    catchError,
    debounceTime,
    filter,
    finalize,
    first,
    map,
    pairwise,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { LoginData } from '../auth/login.model';
import { Register } from '../auth/register.model';
import { EchoService } from './echo.service';

export type PlatformType = 'guest' | 'player' | 'user' | 'admin' | 'master';

@Injectable({ providedIn: 'root' })
export class AuthService extends EchoService implements OnDestroy {
    private user$: Observable<User>;
    private customer$: Observable<Customer>;
    private authUrl: string;
    private usersUrl: string;
    private _loginUrl = '/guest/login';
    private _guestModeLandingUrl = '/guest/guest-landing';
    private _homeUrl = '/home/dashboard';
    private _startUrl = '/guest/start';
    protected customerLandingUrl: string;
    private _customerLandingObs: Observable<CustomerLanding>;
    private get loginUrl(): string {
        return this._loginUrl;
    }
    private get startUrl(): string {
        return this._startUrl;
    }
    public get homeUrl(): string {
        return this._homeUrl;
    }
    public get guestModeLandingUrl(): string {
        return this._guestModeLandingUrl;
    }
    private authenticated: Observable<boolean>;
    private userFetchStatus = new BehaviorSubject<boolean>(false);
    private customerFetchStatus = new BehaviorSubject<boolean>(false);
    private nk3Platform = new BehaviorSubject<PlatformType>('guest');

    private socketSubscription: Subscription;
    private sessionReconciliationSubscription: Subscription;
    private multiTabSyncSubscription: Subscription;
    /** Interval for JWT expiry checks while the tab stays visible (timers may stall when frozen). */
    private readonly sessionCheckIntervalMs = 30_000;
    /**
     *
     * @param localeId
     * @param builder
     * @param config
     * @param error
     * @param logger
     * @param jwtHelper
     * @param userIdle
     * @param injector
     * @param zone
     * @param cookieService
     */

    constructor(
        @Inject(LOCALE_ID) private localeId: string,
        protected builder: RequestBuilderService,
        private error: HttpErrorService,
        private jwtHelper: JwtHelperService,
        private injector: Injector,
        protected zone: NgZone,
        private cookieService: CookieService,
        protected configService: ConfigService,
        protected logger: LoggerService,
        protected dialog: MatDialog,
        protected storage: LocalStorageService
    ) {
        super(configService, logger);
        this.authUrl = `${this.configService.config.apiUrl}auth`;
        this.usersUrl = `${this.configService.config.apiUrl}users`;
        this.customerLandingUrl = `${this.authUrl}/customer/landing`;

        this.loadToken();
        this.authenticated = this.isValid();

        this.createUserObservable();
        this.createCustomerObservable();
        this.subscribeMultiTabLogoutSync();

        /**
         * Handle auth socket subscription based on user state changes.
         * Subscribes when user logs in, unsubscribes when user logs out.
         */
        this.sharedUser
            .pipe(
                takeUntil(this.destroy),
                pairwise(),
                filter(([previousUser, currentUser]) => {
                    // Skip when both are null (no meaningful state change)
                    if (!previousUser && !currentUser) {
                        return false;
                    }

                    // Emit on: login (null → user), logout (user → null), or user ID change
                    return !previousUser || !currentUser || previousUser.id !== currentUser.id;
                })
            )
            .subscribe(([previousUser, currentUser]) => {
                this.logger.log('User state changed:', { previousUser, currentUser });
                if (currentUser) {
                    // Subscribe to auth socket
                    this.subscribeAuthSocket();
                } else {
                    // Unsubscribe from auth socket
                    this.unsubscribeAuthSocket();
                }
            });
    }

    subscribeAuthSocket() {
        this.unsubscribeAuthSocket();

        this.socketSubscription = this.getAuthUserSocket()
            .pipe(
                tap((res) => this.setSharedUser(res.data)),
                switchMap(() => this.refreshToken())
            )
            .subscribe();

        this.socketSubscription.add(
            this.getAuthCustomerSocket()
                .pipe(
                    tap((res) => this.setSharedCustomer(res.data)),
                    switchMap(() => this.refreshToken()),
                    switchMap(() => this.fetchUser())
                )
                .subscribe()
        );

        this.socketSubscription.add(this.tokenExpiredSocket().subscribe(() => this.destroyTokenAndOpenPage()));

        /**
         * Handle logout others
         */
        this.socketSubscription.add(
            combineLatest([this.getUser(), this.logOutOtherSocket()])
                .pipe(
                    tap(([user, res]) => this.logger.log(`Device_id: ${user.device_id}`, res.data?.device_id)),
                    filter(([user, res]) => user.device_id !== res.data?.device_id)
                )
                .subscribe(() => this.destroyTokenAndOpenPage())
        );

        this.subscribeSessionReconciliation();

        /**
         * When the private channel auth is rejected (e.g. the user returns to Safari on iPhone
         * after a long background stay), first attempt a silent token refresh.
         * Refreshing updates the Echo auth header so Pusher can re-authenticate.
         * Only log the user out when the refresh itself fails (truly expired session).
         */
        this.socketSubscription.add(
            this.onEchoAuthFailed()
                .pipe(
                    debounceTime(300),
                    switchMap(() =>
                        this.refreshToken().pipe(
                            catchError(() => {
                                this.zone.run(() => this.destroyTokenAndOpenPage(true));
                                return EMPTY;
                            })
                        )
                    )
                )
                .subscribe()
        );

        /**
         * Handle report zip download
         */
        this.socketSubscription.add(
            this.getAuthReportZipDownloadSocket().subscribe((res) => {
                window.open(res.data.download_url, '_blank');
            })
        );
    }

    /**
     * Subscribes to all triggers that should re-validate the session while logged in.
     */
    private subscribeSessionReconciliation(): void {
        this.unsubscribeSessionReconciliation();

        const tokenExpiring$ = this.tokenExpiringSocket().pipe(
            tap((res) => localStorage.setItem('tokenExpiration', res.data.token_expiry))
        );

        const tabBecameVisible$ = fromEvent(document, 'visibilitychange').pipe(
            filter(() => document.visibilityState === 'visible')
        );

        const tabRecovered$ = merge(
            fromEvent(window, 'pageshow').pipe(
                filter((event: PageTransitionEvent) => {
                    if (document.visibilityState !== 'visible') {
                        return false;
                    }
                    const doc = document as Document & { wasDiscarded?: boolean };
                    return event.persisted || doc.wasDiscarded === true;
                })
            ),
            fromEvent(document, 'resume'),
            fromEvent(window, 'focus').pipe(debounceTime(1000)),
            fromEvent(window, 'online').pipe(debounceTime(300))
        );

        const periodicWhileVisible$ = interval(this.sessionCheckIntervalMs).pipe(
            filter(() => document.visibilityState === 'visible')
        );

        this.sessionReconciliationSubscription = merge(
            tokenExpiring$,
            tabBecameVisible$,
            tabRecovered$,
            periodicWhileVisible$,
            this.onEchoReconnected()
        ).subscribe(() => this.zone.run(() => this.reconcileSessionWhenTabVisible()));

        this.zone.run(() => this.reconcileSessionWhenTabVisible());
    }

    private unsubscribeSessionReconciliation(): void {
        if (this.sessionReconciliationSubscription) {
            this.sessionReconciliationSubscription.unsubscribe();
            this.sessionReconciliationSubscription = null;
        }
    }

    /**
     * When another tab clears the token, log out this tab too (bank-style session sync).
     */
    private subscribeMultiTabLogoutSync(): void {
        this.multiTabSyncSubscription = fromEvent<StorageEvent>(window, 'storage')
            .pipe(
                takeUntil(this.destroy),
                filter((event) => event.key === 'token' && !event.newValue && !!event.oldValue)
            )
            .subscribe(() => {
                if (this.token.value) {
                    this.zone.run(() => this.destroyTokenAndOpenPage(true));
                }
            });
    }

    /**
     * Re-check session when the tab is visible (active again).
     * Covers missed socket events while the tab was hidden, throttled, or frozen.
     */
    private reconcileSessionWhenTabVisible(): void {
        if (document.visibilityState !== 'visible') {
            return;
        }

        const token = this.token.value ?? this.getLocalToken();
        if (!token) {
            return;
        }

        if (this.isTokenExpired(token)) {
            localStorage.removeItem('tokenExpiration');
            this.destroyTokenAndOpenPage(true);
            return;
        }

        const tokenExpiry = localStorage.getItem('tokenExpiration');
        if (!tokenExpiry) {
            return;
        }

        const secondsRemaining = moment(tokenExpiry).diff(moment(), 'seconds');
        if (secondsRemaining <= 0) {
            localStorage.removeItem('tokenExpiration');
            this.destroyTokenAndOpenPage(true);
            return;
        }

        this.handleExpiringCountdown(this.calculateDifferenceInSeconds(tokenExpiry));
    }

    calculateDifferenceInSeconds(expiryDate: string): number {
        // Calculate the difference in seconds between the expiry date and the current date
        const timeRemaining = Math.min(60, Math.max(0, moment(expiryDate).diff(moment(), 'seconds')));
        return timeRemaining;
    }

    private expiryDialogRef: MatDialogRef<DialogTokenExpirationCountdownComponent>;
    handleExpiringCountdown(period?: number): void {
        if (period == null || period < 0) {
            localStorage.removeItem('tokenExpiration');
            return;
        }

        if (period === 0) {
            localStorage.removeItem('tokenExpiration');
            this.destroyTokenAndOpenPage(true);
            return;
        }

        // Check if the dialog is already open; if yes, return
        if (this.expiryDialogRef?.getState() === MatDialogState.OPEN) {
            return;
        }

        // Open the dialog with the specified data
        this.expiryDialogRef = this.dialog.open(DialogTokenExpirationCountdownComponent, {
            data: { period: period, customer$: this.customer$ },
            disableClose: true,
            panelClass: 'sheet-wrapper',
        });

        // Clear reference after the dialog is closed
        this.expiryDialogRef
            .afterClosed()
            .pipe(
                first(),
                switchMap((res) =>
                    res
                        ? this.refreshToken()
                        : this.doLogout().pipe(tap(() => this.destroyTokenAndOpenPage(true, true)))
                )
            )
            .subscribe(() => {
                this.expiryDialogRef = null;
                localStorage.removeItem('tokenExpiration');
            });
    }

    /**
     * Login
     */
    login(login: LoginData, redirect_uri?: string): Observable<ResponseData<any>> {
        const token = this.getLocalToken();
        if (token) {
            login.oldToken = token;
        }

        return this.builder
            .post<any>(`${this.authUrl}/login`, login)
            .request()
            .pipe(
                take(1),
                map((res) => {
                    if (res.data.token) {
                        return this.handleLogin(res, { user: login, redirect_uri: redirect_uri });
                    }
                    return res;
                }),
                catchError((err) => this.error.handle(err))
            );
    }

    /**
     * Route after login
     */
    public routeAfterLogin(redirect_uri?: string) {
        const router = this.injector.get(Router);
        this.getUser()
            .pipe(takeUntil(this.destroy), first())
            .subscribe((user) => {
                if (!!user?.metadata?.welcome_material?.id) {
                    redirect_uri
                        ? router.navigate(['welcome'], {
                              queryParams: {
                                  redirect_uri: redirect_uri,
                              },
                          })
                        : router.navigate(['welcome']);
                } else {
                    const uri = redirect_uri || this.homeUrl;
                    router.navigateByUrl(uri);
                }
            });
    }

    storeLogin(): Observable<any> {
        return this.builder
            .get<any>('https://api.ipregistry.co/?key=tryout')
            .request()
            .pipe(
                take(1),
                map((data) => new UserLogin(data)),
                catchError((err) => EMPTY),
                switchMap((userLogin) => this.builder.post<any>(`${this.usersUrl}/login`, userLogin).request())
            );
    }

    /**
     * Register trail accounts.
     */
    register(register: Register, redirectUri?: string): Observable<ResponseData<User>> {
        return this.builder
            .post<User>(`${this.authUrl}/register`, { ...register, short_name: Utils.getSubdomain() })
            .request()
            .pipe(
                map((res) => this.handleLogin(res, redirectUri ? { redirect_uri: redirectUri } : undefined)),
                // catchError(error => this.error.handle(error)),
                takeUntil(this.destroy)
            );
    }

    /**
     * Leave the academy page for Google sign-in on the API.
     * Google returns to one API callback, which sends the user back here.
     */
    public startGoogleLogin(redirectPath?: string, subscriptionPlanId?: number): void {
        const params = new URLSearchParams();
        params.set('return_origin', window.location.origin);
        params.set('login_path', this.googleLoginPath());
        const shortName = Utils.getSubdomain();
        if (shortName) {
            params.set('short_name', shortName);
        }
        if (redirectPath) {
            params.set('redirect_path', redirectPath);
        }
        if (subscriptionPlanId) {
            params.set('subscription_plan_id', String(subscriptionPlanId));
        }
        window.location.assign(`${this.authUrl}/google/redirect?${params.toString()}`);
    }

    /**
     * Production serves each language under /en, /sv, … and 307-redirects
     * /guest/login to that prefix, dropping query params. Send Google back to
     * the same localized login path the user started from.
     */
    private googleLoginPath(): string {
        const path = window.location.pathname.replace(/\/+$/, '') || '/';
        const loginPath = path.replace(/\/guest\/(?:signup|trial)(?:\/.*)?$/i, '/guest/login');
        if (/\/guest\/login$/i.test(loginPath)) {
            return loginPath;
        }
        const locale = path.match(/^\/([a-z]{2}(?:-[A-Za-z]{2})?)(?=\/|$)/);
        return locale ? `/${locale[1]}/guest/login` : '/guest/login';
    }

    /**
     * Reset authentication and reload.
     * Used for debugging.
     */
    public reset(): void {
        this.destroyToken();
        window.location.reload();
    }

    /**
     * Check if the user is authenticated.
     */
    public check(): Observable<boolean> {
        return this.authenticated;
    }

    /**
     * Emits whenever the realtime (WebSocket) connection comes back after a drop.
     *
     * On iPhone Safari the connection is killed every time the user leaves the
     * app and returns. Components can use this to re-sync any state that depends
     * on live server events that may have been missed while the socket was dead
     * (for example a loading spinner that waits for an AI result to arrive).
     */
    public onSocketReconnected(): Observable<void> {
        return this.onEchoReconnected();
    }

    /**
     * Force a fresh load of the logged-in user from the server.
     *
     * This is used to catch up on user information (such as the status of an
     * in-progress AI generation) after the live connection was lost and the
     * normal real-time update was never delivered.
     */
    public refreshUser(): Observable<User> {
        return this.fetchUser();
    }

    /**
     * Fetch the authenticated useUser from store.
     */
    public getUser(): Observable<User> {
        return this.user$.pipe(
            tap(
                (user) =>
                    (user.privileges =
                        user.role_value !== 'user' && user.role_value !== 'guest'
                            ? { ...user.privileges, create: 'allowed' }
                            : { public: 'not_allowed', publish: 'not_allowed', create: 'not_allowed' })
            )
        );
    }

    /**
     * Fetch the authenticated contacts customer from store.
     */
    public getCustomer(): Observable<Customer> {
        return this.customer$;
    }

    public getCustomerLanding(): Observable<CustomerLanding> {
        if (!this._customerLandingObs) {
            this._customerLandingObs = this.builder
                .get<CustomerLanding>(this.customerLandingUrl)
                .request()
                .pipe(
                    shareReplay(),
                    map((res) => this.mapCustomerLanding(res.data)),
                    catchError(() =>
                        of({
                            title: 'Klickdata',
                            logotype_padding: 0,
                            logotype_url: 'assets/images/klickdata-logo-small.png',
                            background_url: 'assets/images/login-bg.jpg',
                            enable_anonymous_register: false,
                            footerLogotype: 'assets/images/logo_liggande_gra.svg',
                            guest_mode: false,
                        })
                    )
                );
        }
        return this._customerLandingObs;
    }

    private mapCustomerLanding(data: CustomerLanding): CustomerLanding {
        const landingData = data;
        if (data.styles?.landing?.font_size > 35) {
            landingData.styles.landing.font_size = 35;
        }
        if (data.styles?.landing?.font_size < 20) {
            landingData.styles.landing.font_size = 20;
        }
        // landingData.enable_anonymous_register = true;
        return landingData;
    }

    public handleLogin(
        response: ResponseData<any>,
        extraData?: { user?: LoginData; redirect_uri?: string }
    ): ResponseData<any> {
        if (response.data.token) {
            this.clearCache();
            this.setToken(response.data.token, true, extraData?.user);
            // Route after login
            this.routeAfterLogin(extraData?.redirect_uri);
        }

        return response;
    }
    public handleGuestTokenLogoutRequired(
        response: ResponseData<any>,
        extraData?: { user?: LoginData; redirect_uri?: string }
    ): ResponseData<any> {
        if (response.data.token) {
            this.doLogout()
                .pipe(
                    first(),
                    tap(() => this.destroyToken())
                )
                .subscribe(() => {
                    this.setToken(response.data.token, true, extraData?.user);
                    this.routeAfterLogin(extraData?.redirect_uri);
                });
        }

        return response;
    }
    public handleGuestTokenNoLogoutRequired(
        response: ResponseData<any>,
        extraData?: { user?: LoginData; redirect_uri?: string }
    ): ResponseData<any> {
        if (response.data.token) {
            this.clearCache();
            this.setToken(response.data.token, true, extraData?.user);
            this.routeAfterLogin(extraData?.redirect_uri);
        }

        return response;
    }

    public handleOtherUserLogin(redirectUri: string) {
        this.doLogout()
            .pipe(
                first(),
                tap(() => this.destroyToken())
            )
            .subscribe(() => {
                this.injector.get(Router).navigate([this.loginUrl], {
                    queryParams: redirectUri && this.validRedirectUri(redirectUri) ? { redirect_uri: redirectUri } : {},
                });
            });
    }

    /**
     * Get user from cache
     */
    private getSharedUser(): Observable<User> {
        return this.sharedUser.pipe(filter((user) => !!user));
    }

    /**
     * Creates a customer observable.
     * Updates on token updates.
     */
    private createUserObservable() {
        this.user$ = this.authenticated.pipe(
            switchMap((authenticated) => {
                // Check if the token is valid and authenticated
                if (!authenticated) {
                    return EMPTY;
                }

                // Defer ensures that the check for fetch status is done lazily
                return defer(() => {
                    // Get user from cache if available
                    if (this.sharedUser.getValue()) {
                        return this.getSharedUser();
                    }

                    // If a fetch is in progress, wait for it to finish
                    if (this.userFetchStatus.value) {
                        this.logger.info('Waiting for fetch user to complete...');
                        return this.userFetchStatus.pipe(
                            filter((fetching) => !fetching),
                            switchMap(() => this.getSharedUser())
                        );
                    }

                    // No cached user and no ongoing fetch, initiate a new fetch
                    this.logger.info('Fetching User from store');
                    this.userFetchStatus.next(true);
                    return this.fetchUser();
                });
            }),
            // Use shareReplay with refCount: true to ensure the observable completes
            // when there are no subscribers and will re-execute when new subscribers arrive
            // after shared customer has been updated
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private fetchUser() {
        return this.builder
            .get<UserData>(`${this.authUrl}/user`)
            .request()
            .pipe(
                map((res) => this.setSharedUser(res.data)),
                shareReplay(1), // Ensure the result is shared and replayed to late subscribers
                catchError((err) => {
                    this.destroyTokenAndOpenPage(true);
                    return EMPTY;
                }),
                takeUntil(this.destroy),
                finalize(() => {
                    // reset cache expiration flag use cached data until @cacheTTL ended.
                    this.logger.info('Fetch User Done!!!');
                    this.userFetchStatus.next(false);
                })
            );
    }

    private setSharedUser(data: UserData) {
        const user = new User({ ...data, lang: this.localeId });
        // Set user information, as well as tags and further extras
        Sentry.configureScope((scope) => {
            scope.setUser({
                id: `${user.id}`,
                username: user.username,
                email: user.email,
                user_mode: user.role_value,
            });
        });
        this.siteLanguage = user.lang;
        this.sharedUser.next(user);
        this.logger.info('User fetch completed!');
        return user;
    }

    /**
     * Get customer from cache
     */
    private getSharedCustomer(): Observable<Customer> {
        return this.sharedCustomer.pipe(
            filter((customer) => !!customer)
            // tap(() => {
            //     this.logger.info('Customer from cache');
            // })
        );
    }

    /**
     * Creates a customer observable.
     * Updates on token updates and when shared customer is updated.
     */
    private createCustomerObservable() {
        this.customer$ = this.authenticated.pipe(
            switchMap((authenticated) => {
                if (!authenticated) {
                    return EMPTY;
                }

                // Get customer from cache if available
                if (this.sharedCustomer.getValue()) {
                    return this.getSharedCustomer();
                }

                // Check if a fetch request is already in progress
                return defer(() => {
                    if (this.customerFetchStatus.value) {
                        this.logger.info('Waiting for fetch customer to complete...');
                        return this.customerFetchStatus.pipe(
                            filter((fetching) => !fetching),
                            switchMap(() => this.getSharedCustomer())
                        );
                    }

                    this.logger.info('Fetching Customer from store');
                    this.customerFetchStatus.next(true);
                    return this.fetchCustomer();
                });
            }),
            // Use shareReplay with refCount: true to ensure the observable completes
            // when there are no subscribers and will re-execute when new subscribers arrive
            // after shared customer has been updated
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private fetchCustomer() {
        return this.builder
            .get<CustomerData>(`${this.authUrl}/customer`)
            .request()
            .pipe(
                map((res) => this.setSharedCustomer(res.data)),
                shareReplay(1), // Ensure the result is shared and replayed to late subscribers
                catchError((err) => {
                    this.destroyTokenAndOpenPage(true);
                    return EMPTY; // Return empty observable on error
                }),
                takeUntil(this.destroy),
                finalize(() => {
                    this.logger.info('Fetch Customer Done!!!');
                    this.customerFetchStatus.next(false);
                })
            );
    }

    private setSharedCustomer(data: CustomerData) {
        const customer = new Customer(data);
        this.sharedCustomer.next(customer);
        this.logger.info('Customer fetch completed!');
        return customer;
    }

    public ngOnDestroy(): void {
        this.multiTabSyncSubscription?.unsubscribe();
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    public updateUserLanguage(language: Language, user?: User) {
        this.siteLanguage = language.lang;
        if (this.getNK3PlatformValue() !== 'guest' && user) {
            let authUserId: number;
            this.getUser()
                .pipe(
                    first(),
                    switchMap((authUser) => {
                        authUserId = authUser.id;
                        return user ? of(user) : of(authUser);
                    }),
                    filter((user) => user.lang !== language.lang),
                    switchMap((user) =>
                        this.builder
                            .put<UserData>(`${this.usersUrl}/${user.id}`, { id: user.id, lang: language.lang })
                            .request()
                    )
                )
                .subscribe(() => {
                    if (authUserId === user.id) {
                        this.changeSiteLang(language.value);
                    }
                });
        } else {
            this.changeSiteLang(language.value);
        }
    }

    private changeSiteLang(lang: string) {
        const router = this.injector.get(Router);
        window.location.assign(`/${lang}${router.url}`);
    }

    public setNK3Platform(scope: PlatformType) {
        this.nk3Platform.next(scope);
    }

    public getNK3Platform(): Observable<PlatformType> {
        return this.nk3Platform.asObservable();
    }

    public getNK3PlatformValue(): PlatformType {
        return this.nk3Platform.value;
    }

    public checkPlatform(...type: PlatformType[]): boolean {
        return type.includes(this.nk3Platform.value);
    }

    public handleInvalidToken() {
        this.destroyTokenAndOpenPage(true);
    }

    /**
     * Set the sting token and notifies observers.
     */
    public setToken(token: string | null, isLogin?: boolean, user?: LoginData): void {
        if (token === 'null' || (token && this.isTokenExpired(token))) {
            this.token.next(null);
            return;
        }

        // cache academy short name for next login
        if (user?.short_name) {
            localStorage.setItem('academy_short_name', user.short_name);
        }

        this.saveToken(token);
        this.token.next(token);
    }

    /**
     * Destroys the token and notifies observers.
     */
    public destroyToken(): void {
        this.removeToken();
        this.clearCache();
        this.token.next(null);
        this._customerLandingObs = null;

        this.unsubscribeAuthSocket();
    }

    private unsubscribeAuthSocket() {
        this.unsubscribeSessionReconciliation();
        if (this.socketSubscription) {
            this.socketSubscription.unsubscribe();
            this.socketSubscription = null;
        }
    }

    /**
     * Get an observable for tokenString
     * Notifies on token updates.
     *
     * @returns Observable<string>
     */
    public getToken(): Observable<string> {
        return this.token.asObservable();
    }

    /**
     * Get a specific claim from token.
     * Notifies on token updates.
     */
    public getClaim(claim: string): Observable<any> {
        return this.token.pipe(
            filter((token) => !!token && !this.isTokenExpired(token)),
            map((token) => this.jwtHelper.decodeToken(token)[claim])
        );
    }

    public sameLogin(newToken: string): Observable<boolean> {
        return this.getClaim('sub').pipe(map((userId) => userId === this.jwtHelper.decodeToken(newToken)['sub']));
    }

    /**
     * Check if the token is valid.
     * Notifies on token updates.
     */
    public isValid(silent = false): Observable<boolean> {
        return this.token.pipe(
            map((token) => {
                const authenticated = token && !this.isTokenExpired(token);
                if (!authenticated && !silent) {
                    // Listen token authenticated when expired or not authenticated then clear cache.
                    this.destroyTokenAndOpenPage(true);
                }
                return authenticated;
            }),
            // Cache the auth check result and maintain it even without subscribers
            shareReplay({ bufferSize: 1, refCount: false })
        );
    }

    /**
     * Saves the token to localStorage if available
     */
    private saveToken(token: string) {
        // Do not save an empty token.
        if (!token) {
            return;
        }

        // Is local storage available?
        if (localStorage) {
            localStorage.setItem('token', token);
        }
    }

    /**
     * Load token
     */
    private prepareToken(): string {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('jwtToken');
        if (token) {
            this.saveToken(token);
            // Remove token from URL
            urlParams.delete('jwtToken');
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState({}, document.title, newUrl);
            return token;
        }

        return this.getLocalToken();
    }

    private getLocalToken(): string {
        return localStorage.getItem('token');
    }

    /**
     * Load token
     */
    private loadToken() {
        let tokenString = this.prepareToken();

        // Fix token
        if (tokenString === 'null') {
            this.removeToken();
            tokenString = undefined;
        }

        this.token.next(tokenString);
    }

    /**
     * Removes the token from localStorage.
     */
    private removeToken() {
        if (localStorage) {
            localStorage.removeItem('token');
            localStorage.removeItem('tokenExpiration');
        }
    }

    /**
     * Check if the assigned token as expired.
     */
    private isTokenExpired(token: string): boolean {
        try {
            return this.jwtHelper.isTokenExpired(token);
        } catch (error) {
            this.destroyToken();
            return true;
        }
    }

    /**
     * Check and update token.
     */
    private updateToken(): Observable<string> {
        return this.builder
            .post<{ token: string }>(`${this.authUrl}/token/update`, null)
            .request()
            .pipe(
                map((res) => res.data.token),
                catchError((err) => this.error.handle(err))
            );
    }

    /**
     * Update user token
     * will expire/refresh user/customer cache
     */
    public refreshToken(): Observable<string> {
        return this.updateToken().pipe(
            take(1),
            tap((token) => this.setToken(token))
        );
    }

    public logout(saveRedirectUrl = false): void {
        combineLatest([this.doLogout(), this.token])
            .pipe(
                first(),
                filter((_, token) => !!token)
            )
            .subscribe();
        // Logout after notify server.
        this.destroyTokenAndOpenPage(saveRedirectUrl, true);
    }

    private doLogout(): Observable<ResponseData<any>> {
        return this.builder
            .post<any>(`${this.authUrl}/logout`, {})
            .request()
            .pipe(
                first(),
                catchError((err) => this.error.handle(err))
            );
    }

    /**
     * Check current domain under klickdata directly or from agent/academy.
     * @returns true when subdomain from klickdata academy.
     */
    public checkSubdomain(): Observable<boolean> {
        return this.builder
            .get<boolean>(`${this.authUrl}/subdomain/check`)
            .request()
            .pipe(
                first(),
                map((res) => res.data),
                catchError(() => of(true))
            );
    }

    /**
     * Destroy token and open login page when idle timedOut or token expired.
     */
    public destroyTokenAndOpenPage(saveUri = false, landing = false) {
        if (this.token.value) {
            this.destroyToken();
            if (!landing) {
                this.openLoginPage(saveUri);
            } else {
                this.openStartPage(saveUri);
            }
        }
    }

    public openLoginPage(saveUri: boolean, router = this.injector.get(Router), uri = router.url) {
        this.zone.run(() =>
            router.navigate([this.loginUrl], {
                queryParams: saveUri && this.validRedirectUri(uri) ? { redirect_uri: uri } : {},
            })
        );
    }
    public openGuestModeLanding(router = this.injector.get(Router), uri = router.url) {
        this.zone.run(() => router.navigate([this.guestModeLandingUrl]));
    }

    private openStartPage(saveUri: boolean, router = <Router>this.injector.get(Router)) {
        this.zone.run(() =>
            router.navigate([this.startUrl], {
                queryParams: saveUri && this.validRedirectUri(router.url) ? { redirect_uri: router.url } : {},
            })
        );
    }

    private clearCache() {
        this.sharedUser.next(null);
        this.sharedCustomer.next(null);
        this.nk3Platform.next('guest');
        this.clearSessionCache();
        CacheUtils.removeAllCachedFilters();
    }

    private clearSessionCache() {
        this.storage.clear('welcome_material_viewed');
    }

    public handleForbidden() {
        const router = this.injector.get(Router);
        router.navigateByUrl('/unauthorised');
    }

    set siteLanguage(langStr: string) {
        this.cookieService.set('lang_ui', langStr, {
            path: '/',
        });
    }

    get siteLanguage(): string {
        return this.cookieService.get('lang_ui');
    }

    /**
     * Store url for redirect after login.
     * Don't redirect to undefined/empty/root/home/guest urls
     */
    private validRedirectUri(redirect_uri: string) {
        return (
            redirect_uri &&
            !['', '/', '/unauthorised', this.homeUrl].some((url) => url === redirect_uri) &&
            redirect_uri.indexOf('/guest/') === -1
        );
    }
}
